import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus.svg';
import {
  FilterOption,
  FilterPopover,
  Flex,
} from '@elementary/common-ui/components';
import FilterChipRow from '@elementary/common-ui/components/Table/TableFilters/FilterChipRow';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import {
  distinctArray,
  distinctArrayBy,
  getValue,
} from '@elementary/common-ui/utils/common';
import { Tooltip } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  HOP_LIMIT,
  LINEAGE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import type {
  HopLimit,
  DagDepthBoundary,
} from '@elementary/common-ui/components/lineageGraph/types';
import { StreamSelector } from '@elementary/common-ui/components/lineageGraph';
import useReport from '@/shared/hooks/useReport';

const NO_OWNERS_VALUE = 'No owners';
const NO_TAGS_VALUE = 'No tags';

const Wrapper = styled(Flex)<{ $hidden?: boolean }>`
  position: absolute;
  top: 88px;
  left: 24px;
  margin-right: 100px;
  background: ${({ theme }) => theme.palette.common.white};
  border: ${({ theme }) => `1px solid ${theme.palette.common.gray13}`};
  border-radius: 8px;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  z-index: 1;
`;

const LeftSection = styled(Flex)`
  border-right: 1px solid ${({ theme }) => theme.palette.common.gray13};
  padding: 16px;
  gap: 16px;
`;

type Props = { hidden?: boolean; dagDepthBoundary?: DagDepthBoundary };

const LineageFilters = ({ hidden, dagDepthBoundary }: Props) => {
  const { reportData } = useReport();
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasPermission = useEnvPermission();

  const { selectedTreeNode, selectedModelColumn } = useSelectedTreeItem();

  const { urlFilters, addFilter, clearFilter, clearAllFilters, updateFilter } =
    useUrlFilters({
      urlKey: LINEAGE_FILTERS,
    });

  const {
    testResultsData,
    sidebarsData,
    invocationsJobIdentificationData,
    modelsData,
  } = useMemo(
    () => ({
      testResultsData: reportData?.test_results,
      sidebarsData: reportData?.groups,
      invocationsJobIdentificationData:
        reportData?.invocations_job_identification,
      modelsData: reportData?.models,
    }),
    [reportData],
  );

  const testOptions = useMemo(
    () =>
      testResultsData
        ? distinctArrayBy(
            Object.keys(testResultsData)
              .map((model) =>
                testResultsData[model].map((test) => ({
                  value: test.metadata.test_name,
                  label: test.metadata.test_name,
                })),
              )
              .flat(),
            'value',
          )
        : [],
    [testResultsData],
  );

  const tagOptions = useMemo(
    () =>
      Object.keys(sidebarsData?.tags || {})
        .filter((tag: string) => tag !== NO_TAGS_VALUE)
        .map((tag: string) => ({
          value: tag,
          label: tag,
        })),
    [sidebarsData],
  );

  const ownerOptions = useMemo(
    () =>
      Object.keys(sidebarsData?.owners || {})
        .filter((owner: string) => owner !== NO_OWNERS_VALUE)
        .map((owner: string) => ({
          value: owner,
          label: owner,
        })),
    [sidebarsData],
  );

  const jobOptions = useMemo(
    () =>
      Object.keys(invocationsJobIdentificationData || {}).map((key) => ({
        label: key,
        value: key,
      })),
    [invocationsJobIdentificationData],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'passed', label: 'Passed' },
      { value: 'warnings', label: 'Warnings' },
      { value: 'errors', label: 'Failures' },
    ],
    [],
  );

  const getModelPropertyOptions = useCallback(
    (key: string) => {
      if (!modelsData) return [];

      const values = Object.keys(modelsData)
        .map((k) => getValue(modelsData?.[k], key))
        .filter((v) => !!v) as string[];

      const distinct = distinctArray(values);
      return distinct.map((v) => ({ label: v, value: v }));
    },
    [modelsData],
  );

  const filterOptions = useMemo(() => {
    const canReadTests = hasPermission('VIEW_TESTS');
    let filterOptions: FilterOption[] = [];
    if (canReadTests) {
      filterOptions = [
        {
          property: 'status',
          propertyLabel: 'Test status',
          options: statusOptions,
          multiselect: true,
        },
        {
          property: 'test',
          propertyLabel: 'Test',
          options: testOptions,
          multiselect: true,
        },
      ];
    }
    filterOptions = filterOptions.concat([
      {
        property: 'owner',
        propertyLabel: 'Owner',
        options: ownerOptions,
        multiselect: true,
      },
      {
        property: 'tag',
        propertyLabel: 'Tag',
        options: tagOptions,
        multiselect: true,
      },
      {
        property: 'job',
        propertyLabel: 'Job',
        options: jobOptions,
        multiselect: true,
      },
      {
        property: 'databaseName',
        propertyLabel: 'Database name',
        options: getModelPropertyOptions('database_name'),
        multiselect: true,
      },
      {
        property: 'schemaName',
        propertyLabel: 'Schema name',
        options: getModelPropertyOptions('schema_name'),
        multiselect: true,
      },
      {
        property: 'tableName',
        propertyLabel: 'Table name',
        options: getModelPropertyOptions('table_name'),
        multiselect: true,
      },
      {
        property: 'materialization',
        propertyLabel: 'Materialization',
        options: getModelPropertyOptions('materialization'),
        multiselect: true,
      },
      {
        property: 'path',
        propertyLabel: 'Path',
        options: getModelPropertyOptions('normalized_full_path'),
        multiselect: true,
      },
      {
        property: 'nodeType',
        propertyLabel: 'dbt node type',
        options: getModelPropertyOptions('artifact_type'),
        multiselect: true,
      },
      {
        property: 'biNodeType',
        propertyLabel: 'BI node type',
        options: getModelPropertyOptions('meta.sub_type'),
        multiselect: true,
      },
    ]);
    return filterOptions;
  }, [
    getModelPropertyOptions,
    jobOptions,
    ownerOptions,
    statusOptions,
    tagOptions,
    testOptions,
    hasPermission,
  ]);

  const selectedHopLimit = useMemo(() => {
    const param = searchParams.get(HOP_LIMIT);
    return (
      param
        ? JSON.parse(param)
        : {
            upstream: dagDepthBoundary?.incomingDepth ? 1 : 0,
            downstream: dagDepthBoundary?.outgoingDepth ? 1 : 0,
          }
    ) as HopLimit;
  }, [dagDepthBoundary, searchParams]);

  const handleHopFilterChange = useCallback(
    (val?: HopLimit) =>
      val
        ? setSearchParams((prev) => {
            prev.set(HOP_LIMIT, JSON.stringify(val));
            return prev;
          })
        : setSearchParams((prev) => {
            prev.delete(HOP_LIMIT);
            return prev;
          }),
    [setSearchParams],
  );

  useEffect(() => {
    // setTimeout solves an issue when hop limit is not cleared when applying filters
    setTimeout(() => {
      setSearchParams((prev) => {
        prev.delete(HOP_LIMIT);
        return prev;
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(urlFilters)]);

  useEffect(() => {
    if (filtersLoaded) {
      // setTimeout solves an issue when lineage filters and hop limit are not cleared when changing selected tree item
      setTimeout(() => {
        setSearchParams((prev) => {
          prev.delete(LINEAGE_FILTERS);
          prev.delete(HOP_LIMIT);
          return prev;
        });
      }, 0);
    } else {
      setFiltersLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreeNode?.id, selectedModelColumn]);

  return (
    <Wrapper $hidden={hidden}>
      <LeftSection>
        <Tooltip
          title={
            selectedModelColumn &&
            'Lineage is filtered on a specific column, additional lineage filters are not supported'
          }
        >
          <div>
            <FilterPopover
              filterOptions={filterOptions}
              onSubmit={(val) => {
                const newValue = (val.value || val.prevValue).filter(
                  (v) => v !== 'all',
                );

                if (val.prevValue?.length || val.prevValue?.includes('all')) {
                  updateFilter(
                    val.property,
                    val.operator,
                    val.prevValue.filter((v) => v !== 'all'),
                    newValue,
                  );
                } else {
                  addFilter(val.property, val.operator, newValue);
                }
              }}
              disabled={!!selectedModelColumn}
              buttonProps={{ variant: 'contained', startIcon: <PlusIcon /> }}
              popoverProps={{ anchorOrigin: { vertical: 70, horizontal: -17 } }}
            />
          </div>
        </Tooltip>

        {urlFilters && (
          <FilterChipRow
            filterOptions={filterOptions}
            selectedFilters={urlFilters}
            clearFilter={clearFilter}
            onClearAll={clearAllFilters}
            onSubmit={(val) =>
              updateFilter(
                val.property,
                val.operator,
                val.prevValue!,
                val.value,
                val.index,
              )
            }
          />
        )}
      </LeftSection>
      <Flex sx={{ p: 2 }}>
        <StreamSelector
          hopLimit={selectedHopLimit}
          onHopLimitChange={handleHopFilterChange}
          dagDepthBoundary={dagDepthBoundary}
        />
      </Flex>
    </Wrapper>
  );
};

export default LineageFilters;
